import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import {
  ShieldCheckIcon,
  CogIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/solid';

const CONSENT_KEY = 'cookie_consent';
const CONSENT_TIMESTAMP_KEY = 'cookie_consent_ts';
const CONSENT_EXPIRY_DAYS = 365;

type ConsentLevel = 'all' | 'essential';

/** Check if consent has expired (>12 months). */
const isConsentExpired = (): boolean => {
  const ts = localStorage.getItem(CONSENT_TIMESTAMP_KEY);
  if (!ts) return true;
  const elapsed = Date.now() - parseInt(ts, 10);
  return elapsed > CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
};

/** Load Google Analytics if GA ID is available. */
const loadGoogleAnalytics = () => {
  // GA ID is injected into the page by the backend / index.html
  // We look for it in window or load it dynamically
  const gaId = (window as any).__GA_ID__;
  if (!gaId || document.getElementById('ga-script')) return;

  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  const inline = document.createElement('script');
  inline.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaId}');
  `;
  document.head.appendChild(inline);
};

/** Load Google Tag Manager if GTM ID is available. */
const loadGTM = () => {
  const gtmId = (window as any).__GTM_ID__;
  if (!gtmId || document.getElementById('gtm-script')) return;

  // GTM head script
  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.textContent = `
    (function(w,d,s,l,i){w[l]=w[l]||[];
    w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
    var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
    j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
    f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(script);

  // GTM noscript iframe (fallback for no-JS environments)
  if (!document.getElementById('gtm-noscript')) {
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript';
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }
};

/** Remove analytics cookies and scripts. */
const removeAnalytics = () => {
  // Remove GA cookies
  const cookies = document.cookie.split(';');
  cookies.forEach(c => {
    const name = c.split('=')[0].trim();
    if (name.startsWith('_ga') || name.startsWith('_gid') || name.startsWith('_gat')) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
    }
  });
  // Remove GA scripts
  const gaScript = document.getElementById('ga-script');
  if (gaScript) gaScript.remove();
  // Remove GTM scripts
  const gtmScript = document.getElementById('gtm-script');
  if (gtmScript) gtmScript.remove();
  const gtmNoscript = document.getElementById('gtm-noscript');
  if (gtmNoscript) gtmNoscript.remove();
};

const saveConsent = (level: ConsentLevel) => {
  localStorage.setItem(CONSENT_KEY, level);
  localStorage.setItem(CONSENT_TIMESTAMP_KEY, Date.now().toString());

  if (level === 'all') {
    loadGoogleAnalytics();
    loadGTM();
  } else {
    removeAnalytics();
  }
};

/** Get current consent level (or null if not set / expired). */
export const getConsentLevel = (): ConsentLevel | null => {
  const consent = localStorage.getItem(CONSENT_KEY);
  if (!consent || isConsentExpired()) return null;
  return consent as ConsentLevel;
};

/** Initialize analytics on app boot if consent was previously granted. */
export const initAnalyticsFromConsent = () => {
  const level = getConsentLevel();
  if (level === 'all') {
    loadGoogleAnalytics();
    loadGTM();
  }
};

/* ───────── Banner Component ───────── */

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    const consent = getConsentLevel();
    if (!consent) {
      // Small delay so the page loads first
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Listen for "Cookie Settings" link clicks (from footer, etc.)
  useEffect(() => {
    const handler = () => {
      const current = getConsentLevel();
      setAnalyticsEnabled(current === 'all' || current === null);
      setShowManage(true);
      setVisible(false);
    };
    window.addEventListener('open-cookie-settings', handler);
    return () => window.removeEventListener('open-cookie-settings', handler);
  }, []);

  const handleAcceptAll = useCallback(() => {
    saveConsent('all');
    setVisible(false);
    setShowManage(false);
  }, []);

  const handleEssentialOnly = useCallback(() => {
    saveConsent('essential');
    setVisible(false);
    setShowManage(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    saveConsent(analyticsEnabled ? 'all' : 'essential');
    setVisible(false);
    setShowManage(false);
  }, [analyticsEnabled]);

  const handleOpenManage = useCallback(() => {
    const current = getConsentLevel();
    setAnalyticsEnabled(current === 'all' || current === null);
    setShowManage(true);
  }, []);

  // ── Banner ──
  if (!visible && !showManage) return null;

  return (
    <>
      {/* Bottom banner */}
      {visible && !showManage && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: 'white',
            borderTop: '1px solid #e2e8f0',
            boxShadow: '0 -4px 24px rgba(0,0,0,0.10)',
            padding: '20px 0',
            animation: 'slideUp 0.35s ease-out',
          }}
        >
          <div className="container" style={{ maxWidth: '960px' }}>
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
              {/* Icon + text */}
              <div className="d-flex align-items-start gap-3" style={{ flex: 1 }}>
                <div
                  style={{
                    width: '40px', height: '40px', borderRadius: '10px',
                    background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <ShieldCheckIcon style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
                </div>
                <div>
                  <h6 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', marginBottom: '4px' }}>
                    We use cookies
                  </h6>
                  <p style={{ color: '#64748b', fontSize: '0.83rem', marginBottom: 0, lineHeight: 1.5 }}>
                    We use essential cookies to keep Sourdoughsy secure and working properly.
                    With your permission, we may also use analytics cookies to improve the platform.
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex flex-wrap gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleEssentialOnly}
                  style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem', padding: '6px 14px' }}
                >
                  <XMarkIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                  Essential only
                </Button>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={handleOpenManage}
                  style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem', padding: '6px 14px' }}
                >
                  <CogIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                  Manage
                </Button>
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  style={{
                    borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem', padding: '6px 14px',
                    background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', border: 'none',
                  }}
                >
                  <CheckIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                  Accept all
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage cookies modal */}
      <Modal show={showManage} onHide={() => { setShowManage(false); if (!getConsentLevel()) setVisible(true); }} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #f1f5f9' }}>
          <Modal.Title style={{ fontWeight: 700, fontSize: '1.1rem' }}>
            <CogIcon style={{ width: '20px', height: '20px', color: '#3b82f6', marginRight: '8px' }} />
            Cookie Preferences
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Essential */}
          <div
            className="d-flex justify-content-between align-items-center p-3 mb-3"
            style={{ background: '#f8fafc', borderRadius: '12px' }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>Essential cookies</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                Login, security, CSRF protection, Stripe checkout. Required for the platform to work.
              </div>
            </div>
            <span
              style={{
                background: '#10b981', color: 'white', fontSize: '0.7rem',
                fontWeight: 700, padding: '3px 10px', borderRadius: '6px', whiteSpace: 'nowrap',
              }}
            >
              Always active
            </span>
          </div>

          {/* Analytics */}
          <div
            className="d-flex justify-content-between align-items-center p-3"
            style={{ background: '#f8fafc', borderRadius: '12px' }}
          >
            <div style={{ marginRight: '16px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>Analytics cookies</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                Help us understand how visitors use the platform so we can improve it. Anonymous & aggregated.
              </div>
            </div>
            <Form.Check
              type="switch"
              id="analytics-toggle"
              checked={analyticsEnabled}
              onChange={e => setAnalyticsEnabled(e.target.checked)}
              style={{ transform: 'scale(1.3)' }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f1f5f9', gap: '8px' }}>
          <Button
            variant="outline-primary"
            onClick={handleAcceptAll}
            style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem' }}
          >
            Accept all
          </Button>
          <Button
            onClick={handleSavePreferences}
            style={{
              borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem',
              background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', border: 'none',
            }}
          >
            Save preferences
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default CookieConsent;

/* ── Standalone opener (for footer "Cookie Settings" link) ── */
export const openCookieSettings = () => {
  // Dispatch a custom event that the CookieConsent component listens for
  window.dispatchEvent(new CustomEvent('open-cookie-settings'));
};
